// Vim Sensei Client - Browser Entry Point

import confetti from 'canvas-confetti';
import { branding } from '../shared/branding';
import type {
  EventCategory,
  NotificationName,
  LessonEventName,
  DrillEventName,
  DrillPayloads,
  DrillTier,
  OverlayConfig,
  ServerMessage,
  ClientMessage,
  LessonPayloads,
  AuthUser,
} from '../shared/types';
import {
  initAuth,
  isAuthInitialized,
  onAuthStateChanged,
  signInWithGoogle,
  signOut,
  getIdToken,
  getCurrentUser,
  authReady,
} from './auth';
import { getLessonEngine, hasDrills } from '../shared/constants';
import { getOrCreateAnonId, acquisitionProps, trackEvent } from './visitor';
// Note: isPublicLesson derived from manifest requires_auth field

// Declare xterm.js globals loaded from CDN
declare const Terminal: new (options: TerminalOptions) => TerminalInstance;
declare const FitAddon: { FitAddon: new () => FitAddonInstance };

interface TerminalOptions {
  cursorBlink?: boolean;
  fontSize?: number;
  fontFamily?: string;
  theme?: Record<string, string>;
}

interface TerminalInstance {
  cols: number;
  rows: number;
  options: { scrollback?: number; [key: string]: unknown };
  loadAddon(addon: FitAddonInstance): void;
  open(element: HTMLElement | null): void;
  write(data: string): void;
  focus(): void;
  resize(cols: number, rows: number): void;
  reset(): void;
  onData(callback: (data: string) => void): void;
  attachCustomKeyEventHandler(handler: (event: KeyboardEvent) => boolean): void;
}

interface FitAddonInstance {
  fit(): void;
  proposeDimensions(): { cols: number; rows: number } | undefined;
}

interface RelatedResource {
  slug: string;
  number: string;
  title: string;
  titleAccent?: string;
  category: string;
  readTime: number;
}

interface LessonInstructions {
  lesson_id: string;
  name: string;
  topic: string;
  title: string;
  objective: string;
  content: string;
  hints?: string[];
  totalSteps?: number;
  currentStep?: number;
  relatedResources?: RelatedResource[];
}

interface LessonManifestEntry {
  lesson_id: string;
  name: string;
  title: string;
  tags: string[];
  is_free: boolean;
  requires_auth: boolean;
  prerequisites: string[];
}

interface LessonGroup {
  name: string;
  lessons: string[];
}

interface LessonManifest {
  groups: LessonGroup[];
  lessons: LessonManifestEntry[];
}

// Global manifest data (populated on load)
let lessonManifest: LessonManifest | null = null;

/**
 * Check if a lesson is public (no auth required) based on manifest data
 */
function isPublicLesson(lessonId: string): boolean {
  if (!lessonManifest) return false;
  const lesson = lessonManifest.lessons.find(
    l => `${l.lesson_id}-${l.name}` === lessonId
  );
  return lesson?.requires_auth === false;
}

/**
 * Check if a lesson is free (no payment required) based on manifest data
 */
function isFreeLesson(lessonId: string): boolean {
  if (!lessonManifest) return false;
  const lesson = lessonManifest.lessons.find(
    l => `${l.lesson_id}-${l.name}` === lessonId
  );
  return lesson?.is_free === true;
}

// Track whether current user has paid
let userHasPaid = false;

/**
 * Fetch payment status from server
 */
async function fetchPaymentStatus(): Promise<void> {
  const token = await getIdToken();
  if (!token) {
    console.log('[BILLING] No auth token, skipping payment status fetch');
    userHasPaid = false;
    return;
  }

  try {
    console.log('[BILLING] Fetching payment status...');
    const res = await fetch('/api/billing/status', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      const data = await res.json();
      userHasPaid = data.hasPaid === true;
      console.log('[BILLING] Payment status:', { hasPaid: userHasPaid, raw: data });
    } else {
      console.error('[BILLING] Status request failed:', res.status, res.statusText);
    }
  } catch (err) {
    console.error('[BILLING] Failed to fetch payment status:', err);
  }
}

// ============================================================================
// Terminal Setup
// ============================================================================

const term = new Terminal({
  cursorBlink: true,
  fontSize: 14,
  // "JetBrains Mono Nerd Font" is self-hosted (public/fonts/, wired via
  // @font-face in styles.css) — a superset of plain JetBrains Mono patched
  // with Nerd Font icon glyphs, needed for file/plugin icons some Neovim
  // lessons render (e.g. mini.icons in the LazyVim sidebar). Falls back to
  // plain "JetBrains Mono" (Google Fonts) if the self-hosted font fails to
  // load — icons would show as tofu boxes but the terminal stays usable.
  fontFamily: '"JetBrains Mono Nerd Font", "JetBrains Mono", Menlo, Monaco, "Courier New", monospace',
  theme: {
    background: '#000000',
    foreground: '#ebdbb2',
    cursor: '#8eff71',
    cursorAccent: '#000000',
    selection: 'rgba(142, 255, 113, 0.3)',
    black: '#282828',
    red: '#ff7351',
    green: '#8eff71',
    yellow: '#d79921',
    blue: '#83a598',
    magenta: '#d3869b',
    cyan: '#17eefa',
    white: '#ebdbb2',
    brightBlack: '#928374',
    brightRed: '#fb4934',
    brightGreen: '#b8bb26',
    brightYellow: '#fabd2f',
    brightBlue: '#83a598',
    brightMagenta: '#d3869b',
    brightCyan: '#8ec07c',
    brightWhite: '#fbf1c7',
  },
});

const fitAddon = new FitAddon.FitAddon();
term.loadAddon(fitAddon);

term.open(document.getElementById('terminal'));
fitAddon.fit();

// Full-screen TUIs (vim/nvim) repaint the screen and don't need an xterm-side
// scrollback. Without this, the wheel detaches the viewport from the live
// cursor and corrupts redraws.
term.options.scrollback = 0;

// ============================================================================
// WebSocket Connection
// ============================================================================

const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
let ws: WebSocket | null = null;
let reconnectAttempts = 0;
let intentionalClose = false;
const maxReconnectAttempts = 10;
const baseReconnectDelay = 1000;

function sendMessage(message: ClientMessage): void {
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify(message));
  }
}

async function connect(): Promise<void> {
  // Get auth token if authenticated (optional)
  const token = await getIdToken();

  // Build WebSocket URL with token if available, plus the anonymous visitor
  // ID so analytics can follow signed-out sessions.
  const params = new URLSearchParams();
  if (token) params.set('token', token);
  const visitorId = getOrCreateAnonId();
  if (visitorId) params.set('vid', visitorId);
  const query = params.toString();
  const wsUrl = `${protocol}//${window.location.host}${query ? `?${query}` : ''}`;

  ws = new WebSocket(wsUrl);

  ws.onopen = async (): Promise<void> => {
    console.log('Connected to server');
    reconnectAttempts = 0;
    // Force-load the terminal fonts before measuring. Google Fonts ships a
    // CSS link but lazy-loads the actual font file until something uses it,
    // and the self-hosted Nerd Font is behind its own @font-face — xterm
    // hasn't rendered yet, so document.fonts.ready resolves immediately
    // against an empty load queue. Without this explicit load, fit()
    // measures with fallback-font metrics and term.cols/rows are wrong,
    // which become the initial spawn dims.
    if (document.fonts?.load) {
      try {
        await Promise.all([
          document.fonts.load('14px "JetBrains Mono Nerd Font"'),
          document.fonts.load('14px "JetBrains Mono"'),
        ]);
      } catch {
        // Font load failed (offline, CDN/asset down) — fall through with fallback metrics.
      }
    }
    // One more animation frame so the renderer picks up the new font's
    // cell dimensions before we measure.
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });
    fitAddon.fit();
    sendMessage({ type: 'resize', cols: term.cols, rows: term.rows });

    // If user has cached credentials, upgrade session immediately
    const user = getCurrentUser();
    if (user) {
      const token = await getIdToken();
      if (token) {
        console.log('[WS] Sending cached auth credentials...');
        sendMessage({ type: 'authenticate', token });
      }
    }
  };

  ws.onmessage = (event: MessageEvent): void => {
    const msg = JSON.parse(event.data as string) as ServerMessage;

    if (msg.type === 'terminal') {
      term.write(msg.data);
    } else if (msg.type === 'event') {
      console.log('[WS] Received event:', msg.category, msg.name, msg.data);
      handleEvent(msg.category, msg.name, msg.data);
    } else if (msg.type === 'auth_required') {
      console.log('[WS] Auth required for lesson:', msg.lessonId);
      showAuthModal(msg.lessonId);
    } else if (msg.type === 'payment_required') {
      console.log('[WS] Payment required for lesson:', msg.lessonId);
      showPaymentModal(msg.lessonId);
    } else if (msg.type === 'auth_success') {
      console.log('[WS] Auth success:', msg.user.email || msg.user.uid);
      // If there was a pending lesson, select it now
      if (pendingLessonId) {
        const lessonToSelect = pendingLessonId;
        pendingLessonId = null;
        selectLesson(lessonToSelect);
      }
    } else if (msg.type === 'error') {
      console.error('[WS] Server error:', msg.message);
      // Show auth errors to user
      if (msg.message.includes('Authentication') || msg.message.includes('token')) {
        term.write(`\r\n\x1b[31m${msg.message}\x1b[0m\r\n`);
      }
    }
  };

  ws.onclose = (event): void => {
    console.log('Disconnected from server', event.code, event.reason);

    // Don't show message or auto-reconnect for intentional closes (e.g., sign-out)
    if (intentionalClose) {
      intentionalClose = false;
      return;
    }

    // Don't reconnect on auth errors
    if (event.code === 4001 || event.code === 4002) {
      term.write('\r\n\x1b[31mAuthentication failed. Please sign in again.\x1b[0m\r\n');
      return;
    }

    if (reconnectAttempts < maxReconnectAttempts) {
      const delay = baseReconnectDelay * Math.pow(2, reconnectAttempts);
      reconnectAttempts++;
      term.write(`\r\n\x1b[33mConnection lost. Reconnecting in ${delay / 1000}s... (${reconnectAttempts}/${maxReconnectAttempts})\x1b[0m\r\n`);
      setTimeout(connect, delay);
    } else {
      term.write('\r\n\x1b[31mConnection lost. Please refresh the page.\x1b[0m\r\n');
    }
  };

  ws.onerror = (error: Event): void => {
    console.error('WebSocket error:', error);
  };
}

// Don't connect immediately - wait for auth
// connect();

// ============================================================================
// Event Handling
// ============================================================================

const notificationConfig: Record<NotificationName, OverlayConfig> = {
  collision: {
    icon: 'warning',
    title: 'Collision Detected',
    message: 'Your cursor has hit a wall.',
    code: 'ERR_0x404',
    type: 'error',
    autoDismiss: 5000,
  },
  invalid_key: {
    icon: 'keyboard_off',
    title: 'Invalid Key',
    message: 'Only h, j, k, l keys are allowed. Use the home row!',
    code: 'ERR_0x403',
    type: 'error',
    autoDismiss: 3000,
  },
  error: {
    icon: 'error',
    title: 'Incorrect Output',
    message: 'The buffer content does not match the expected output.',
    code: 'ERR_0x500',
    type: 'error',
    autoDismiss: 5000,
  },
};

const lessonEventConfig: Partial<Record<LessonEventName, OverlayConfig>> = {
  complete: {
    icon: 'emoji_events',
    title: 'Lesson Complete',
    message: 'Well done, warrior! You have completed this lesson.',
    code: 'SUCCESS',
    type: 'success',
  },
  step_ready: {
    icon: 'check_circle',
    title: 'Step Complete',
    message: 'Press any key to continue...',
    code: 'STEP_OK',
    type: 'success',
    // No autoDismiss - stays up until the learner dismisses it, which is
    // what actually releases dojo.vim's blocking getchar() (see
    // handleLessonEvent's step_ready case and hideOverlay's confirm_step
    // send below).
  },
  step_complete: {
    icon: 'check_circle',
    title: 'Step Complete',
    message: 'Moving to next step...',
    code: 'STEP_OK',
    type: 'success',
    autoDismiss: 1000,
  },
  // 'start' and 'step_start' events don't show an overlay
};

let overlayTimeout: ReturnType<typeof setTimeout> | null = null;

// Set while the step_ready overlay is up. hideOverlay() checks this to know
// whether dismissing the overlay should release dojo.vim's blocking
// getchar() via a confirm_step message.
let awaitingStepConfirm = false;

/**
 * Main event dispatcher - routes events to appropriate handlers
 */
function handleEvent(category: EventCategory, name: string, data: unknown): void {
  console.log('[EVENT] handleEvent called:', category, name, data);

  if (category === 'notification') {
    handleNotification(name as NotificationName, data);
  } else if (category === 'lesson') {
    handleLessonEvent(name as LessonEventName, data as LessonPayloads[LessonEventName]);
  } else if (category === 'drill') {
    handleDrillEvent(name as DrillEventName, data as DrillPayloads[DrillEventName]);
  } else {
    console.warn('[EVENT] Unknown event category:', category);
  }
}

/**
 * Handle notification events (transient UI feedback)
 */
function handleNotification(name: NotificationName, data: unknown): void {
  const config = notificationConfig[name];
  if (!config) {
    console.warn('[EVENT] Unknown notification:', name);
    return;
  }

  // For error notifications, use the custom message from the payload
  if (name === 'error' && data && typeof data === 'object' && 'message' in data) {
    const errorData = data as { message: string };
    showOverlay({ ...config, message: errorData.message });
    return;
  }

  // For invalid_key notifications, use the key from the payload
  if (name === 'invalid_key' && data && typeof data === 'object' && 'key' in data) {
    const keyData = data as { key: string };
    showOverlay({ ...config, message: `The key '${keyData.key}' is not allowed in this step.` });
    return;
  }

  showOverlay(config);
}

/**
 * Handle lesson lifecycle events
 */
function handleLessonEvent(name: LessonEventName, data: LessonPayloads[LessonEventName]): void {
  if (name === 'complete') {
    const completeData = data as LessonPayloads['complete'];
    const lessonId = completeData.lessonId || currentLessonId;
    const config = lessonEventConfig.complete!;
    const customConfig: OverlayConfig = {
      ...config,
      message: `You have completed ${lessonId}! Well done, warrior.`,
    };
    showOverlay(customConfig);

    // Fire confetti celebration
    confetti({
      particleCount: 150,
      spread: 100,
      origin: { y: 0.6 },
      zIndex: 200,
    });

    // Mark lesson as completed in localStorage
    markLessonCompleted(lessonId);

    // Block input streaming to PTY
    setInputBlocked(true);

    // If this lesson has drills, render the post-lesson drill CTA into the
    // instructor panel. The user can Start Drills or Skip.
    if (lessonId && hasDrills(lessonId)) {
      renderDrillCTA(lessonId);
    }
  } else if (name === 'start') {
    // Resume input streaming when new lesson starts
    setInputBlocked(false);
    term.focus();
  } else if (name === 'step_ready') {
    // Win condition met; dojo.vim is blocked waiting for us to dismiss this
    // overlay (any key, or the global keydown listener) before it advances -
    // see hideOverlay's confirm_step send.
    const stepData = data as LessonPayloads['step_ready'];
    const config = lessonEventConfig.step_ready!;
    const customConfig: OverlayConfig = {
      ...config,
      message: `Step ${stepData.step} of ${stepData.totalSteps} complete! Press any key to continue.`,
    };
    awaitingStepConfirm = true;
    setInputBlocked(true);
    showOverlay(customConfig);
  } else if (name === 'step_complete') {
    const stepData = data as LessonPayloads['step_complete'];
    const config = lessonEventConfig.step_complete!;
    const customConfig: OverlayConfig = {
      ...config,
      message: `Step ${stepData.step} of ${stepData.totalSteps} complete!`,
    };
    showOverlay(customConfig);
  } else if (name === 'step_start') {
    const stepData = data as LessonPayloads['step_start'];
    // Update step progress indicator
    updateStepProgress(stepData.step, stepData.totalSteps);
    // Load new step instructions
    loadStepInstructions(stepData.lessonId, stepData.step);
  } else if (name === 'step_hint') {
    // Reveals the next piece of the current step's instructions - does not
    // touch step state, just appends into what's already on screen.
    const hintData = data as LessonPayloads['step_hint'];
    revealHint(hintData.hintIndex);
  } else {
    console.warn('[EVENT] Unknown lesson event:', name);
  }
}

// ============================================================================
// Drill Mode
// ============================================================================

// Tracks whether the client is currently in drill mode. Used to gate UI
// updates that should not fire when the user is back in lesson flow.
let inDrillMode = false;
let drillTotal = 0;
// Visual-only counter — the server's count in drill:complete is authoritative.
let drillLiveKeystrokes = 0;

function escapeHtml(s: string): string {
  return s.replace(/[<>&"']/g, (c) =>
    ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;' }[c] || c),
  );
}

function renderDrillCTA(lessonId: string): void {
  const content = document.getElementById('lesson-content');
  const title = document.getElementById('lesson-title');
  const number = document.getElementById('lesson-number');
  const topic = document.getElementById('lesson-topic');
  if (!content) return;

  if (number) number.textContent = 'DRILL MODE';
  if (topic) topic.textContent = 'Practice what you just learned';
  if (title) title.innerHTML = 'Lesson complete. Ready to drill?';

  content.innerHTML = `
    <div class="space-y-4">
      <p class="text-on-surface-variant">
        Run a quick 8-drill set to sharpen the motion you just learned. Earn a Bronze, Silver, or Gold tier.
      </p>
      <div class="flex flex-col gap-3 pt-2">
        <button id="drill-cta-start"
          class="w-full bg-primary text-on-primary font-mono font-bold py-3 text-xs tracking-widest uppercase hover:bg-primary-dim transition-colors active:scale-[0.98]">
          Start drills
        </button>
        <button id="drill-cta-skip"
          class="w-full bg-surface-container-highest text-on-surface-variant font-mono py-3 text-[11px] tracking-widest uppercase hover:text-primary transition-colors">
          Skip for now
        </button>
      </div>
    </div>
  `;

  document.getElementById('drill-cta-start')?.addEventListener('click', () => {
    // Reset terminal before drill.vim spawns so its initial render is the
    // first thing the user sees (matches the selectLesson pattern).
    term.reset();
    sendMessage({ type: 'start_drill_mode', lessonId });
    if (content) {
      content.innerHTML = '<p class="text-outline font-mono text-xs">Starting drill mode...</p>';
    }
  });
  document.getElementById('drill-cta-skip')?.addEventListener('click', () => {
    // Restore standard lesson view (re-fetch instructions for the lesson).
    loadLessonInstructions(lessonId);
  });
}

function renderDrillView(payload: DrillPayloads['start']): void {
  inDrillMode = true;
  drillTotal = payload.total;
  drillLiveKeystrokes = 0;

  const number = document.getElementById('lesson-number');
  const topic = document.getElementById('lesson-topic');
  const title = document.getElementById('lesson-title');
  const content = document.getElementById('lesson-content');
  const stepProgress = document.getElementById('step-progress');

  if (number) number.textContent = 'DRILL';
  if (topic) topic.textContent = `Drill ${payload.index + 1} of ${payload.total}`;
  if (title) title.innerHTML = escapeHtml(payload.prompt);
  if (stepProgress) {
    stepProgress.textContent = `Drill ${payload.index + 1} / ${payload.total}`;
    stepProgress.classList.remove('hidden');
  }

  const keysHtml = payload.allowedKeys
    .map(
      (k) =>
        `<span class="inline-block px-2 py-0.5 mr-1 bg-primary/10 text-primary border border-primary/30 font-mono text-xs">${escapeHtml(k)}</span>`,
    )
    .join('');

  if (content) {
    content.innerHTML = `
      <div class="space-y-4">
        <div>
          <div class="text-[10px] uppercase tracking-widest text-outline font-mono mb-2">Allowed keys</div>
          <div>${keysHtml}</div>
        </div>
        <div class="bg-surface-container p-4 border-l-2 border-primary/40">
          <div class="text-[10px] uppercase tracking-widest text-outline font-mono mb-2">Live stats</div>
          <div class="flex gap-6 font-mono text-xs">
            <div>
              <span class="text-outline">keystrokes</span>
              <span id="drill-live-keys" class="text-primary ml-2">0</span>
            </div>
            <div>
              <span class="text-outline">banned hits</span>
              <span id="drill-live-bad" class="text-error ml-2">0</span>
            </div>
          </div>
        </div>
        <p class="text-[11px] text-outline font-mono">Reach the target cursor position to advance.</p>
      </div>
    `;
  }

  setInputBlocked(false);
  term.focus();

  // Update the small task-progress bar at the bottom.
  updateStepProgress(payload.index + 1, payload.total);
}

function renderDrillScoreScreen(payload: DrillPayloads['set_complete']): void {
  inDrillMode = false;
  setInputBlocked(true);
  setDrillTierLocal(payload.lessonId, payload.tier);

  const number = document.getElementById('lesson-number');
  const topic = document.getElementById('lesson-topic');
  const title = document.getElementById('lesson-title');
  const content = document.getElementById('lesson-content');
  const stepProgress = document.getElementById('step-progress');

  if (number) number.textContent = 'DRILL RESULT';
  if (topic) topic.textContent = 'Set complete';
  if (title) title.innerHTML = `${payload.tier.toUpperCase()} earned`;
  if (stepProgress) stepProgress.classList.add('hidden');

  const tierColor =
    payload.tier === 'gold' ? '#fabd2f' : payload.tier === 'silver' ? '#c0c0c0' : '#cd7f32';

  const rows = payload.drills
    .map(
      (r) => `
    <tr class="border-b border-outline-variant/10">
      <td class="py-2 pr-3 text-outline">#${r.index + 1}</td>
      <td class="py-2 pr-3">
        <span class="${r.keystrokes <= r.optimalKeystrokes ? 'text-primary' : 'text-on-surface-variant'}">${r.keystrokes}</span>
        <span class="text-outline"> / ${r.optimalKeystrokes}</span>
      </td>
      <td class="py-2 pr-3 ${r.invalidKeys === 0 ? 'text-primary' : 'text-error'}">${r.invalidKeys}</td>
      <td class="py-2 text-outline">${(r.durationMs / 1000).toFixed(1)}s</td>
    </tr>`,
    )
    .join('');

  if (content) {
    content.innerHTML = `
      <div class="space-y-5">
        <div class="text-center py-4 border border-outline-variant/20 bg-surface-container">
          <div class="font-headline font-black text-4xl mb-2" style="color: ${tierColor}">
            ${payload.tier.toUpperCase()}
          </div>
          <div class="font-mono text-[11px] text-outline uppercase tracking-widest">
            ${payload.totalKeystrokes} keys · ${payload.totalInvalidKeys} banned · ${(payload.totalDurationMs / 1000).toFixed(1)}s
          </div>
        </div>
        <table class="w-full text-xs font-mono">
          <thead>
            <tr class="text-outline text-[10px] uppercase tracking-widest border-b border-outline-variant/20">
              <th class="text-left py-2 pr-3">Drill</th>
              <th class="text-left py-2 pr-3">Keys / Opt</th>
              <th class="text-left py-2 pr-3">Banned</th>
              <th class="text-left py-2">Time</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
        <div class="flex flex-col gap-3 pt-2">
          <button id="drill-retry"
            class="w-full bg-primary text-on-primary font-mono font-bold py-3 text-xs tracking-widest uppercase hover:bg-primary-dim transition-colors active:scale-[0.98]">
            Retry drills
          </button>
          <button id="drill-continue"
            class="w-full bg-surface-container-highest text-on-surface-variant font-mono py-3 text-[11px] tracking-widest uppercase hover:text-primary transition-colors">
            Back to lesson
          </button>
        </div>
      </div>
    `;
  }

  document.getElementById('drill-retry')?.addEventListener('click', () => {
    term.reset();
    sendMessage({ type: 'start_drill_mode', lessonId: payload.lessonId });
    if (content) {
      content.innerHTML = '<p class="text-outline font-mono text-xs">Restarting drills...</p>';
    }
  });
  document.getElementById('drill-continue')?.addEventListener('click', () => {
    sendMessage({ type: 'exit_drill_mode' });
    loadLessonInstructions(payload.lessonId);
  });
}

function handleDrillEvent(name: DrillEventName, data: DrillPayloads[DrillEventName]): void {
  if (name === 'start') {
    renderDrillView(data as DrillPayloads['start']);
  } else if (name === 'complete') {
    const completeData = data as DrillPayloads['complete'];
    // Brief positive feedback. The next drill:start (or set_complete) replaces
    // this view in ~200ms via the per-drill vim restart.
    const keysEl = document.getElementById('drill-live-keys');
    const badEl = document.getElementById('drill-live-bad');
    if (keysEl) keysEl.textContent = String(completeData.keystrokes);
    if (badEl) badEl.textContent = String(completeData.invalidKeys);
    setInputBlocked(true); // Block during the brief restart window.
  } else if (name === 'set_complete') {
    renderDrillScoreScreen(data as DrillPayloads['set_complete']);
  } else {
    console.warn('[DRILL] Unknown drill event:', name);
  }
}

// Live keystroke counter — increments on each keypress while in drill mode.
// Skips ESC-sequence responses that xterm.js sends to satisfy vim's terminal-
// capability queries (those aren't user keystrokes). The server's count in
// drill:complete is authoritative; this is visual feedback only.
term.onData((data: string) => {
  if (!inDrillMode || inputBlocked) return;
  if (data.length === 0 || data.charCodeAt(0) === 0x1b) return;
  drillLiveKeystrokes += 1;
  const el = document.getElementById('drill-live-keys');
  if (el) el.textContent = String(drillLiveKeystrokes);
});

/**
 * Display overlay with given configuration
 */
function showOverlay(config: OverlayConfig): void {
  const overlay = document.getElementById('event-overlay');
  const icon = document.getElementById('event-overlay-icon');
  const title = document.getElementById('event-overlay-title');
  const message = document.getElementById('event-overlay-message');
  const code = document.getElementById('event-overlay-code');

  if (!overlay || !icon || !title || !message || !code) {
    console.error('[EVENT] Overlay elements not found');
    return;
  }

  icon.textContent = config.icon;
  title.textContent = config.title;
  message.textContent = config.message;
  code.textContent = config.code;

  overlay.setAttribute('data-type', config.type);

  // Force re-show by hiding first and triggering reflow
  overlay.classList.add('hidden');
  void (overlay as HTMLElement).offsetWidth; // Force reflow
  overlay.classList.remove('hidden');
  console.log('[EVENT] Overlay shown');

  if (overlayTimeout) {
    clearTimeout(overlayTimeout);
    overlayTimeout = null;
  }

  if (config.autoDismiss) {
    overlayTimeout = setTimeout(() => {
      hideOverlay();
    }, config.autoDismiss);
  }
}

function hideOverlay(): void {
  console.log('[EVENT] hideOverlay called');
  const overlay = document.getElementById('event-overlay');
  if (overlay) {
    overlay.classList.add('hidden');
  }
  if (overlayTimeout) {
    clearTimeout(overlayTimeout);
    overlayTimeout = null;
  }
  if (awaitingStepConfirm) {
    awaitingStepConfirm = false;
    sendMessage({ type: 'confirm_step' });
    // Deferred: we're in the capture phase of the same keydown that's
    // dismissing this overlay, which still has to bubble through to xterm's
    // own handler. Unblocking synchronously here would let that same
    // keystroke also leak through to term.onData and get sent as real PTY
    // input - harmless for Row 2 (the process is about to be replaced
    // anyway) but for Row 1's single long-lived process, that leaked key
    // lands directly in the freshly-loaded next step (e.g. a leaked Enter
    // triggers Vim's default down-and-first-non-blank motion, causing a
    // bogus collision the instant the new step loads).
    setTimeout(() => setInputBlocked(false), 0);
  }
  term.focus();
}

// Hide event overlay on any key press. Capture phase: xterm's own keydown
// handling on its hidden textarea stops propagation during the bubble phase,
// so a bubble-phase listener on document never sees the event while the
// terminal has focus (which is most of the time). Capture runs before that.
document.addEventListener(
  'keydown',
  () => {
    const overlay = document.getElementById('event-overlay');
    if (overlay && !overlay.classList.contains('hidden')) {
      hideOverlay();
    }
  },
  true
);

// ============================================================================
// Terminal Input/Output
// ============================================================================

// Flag to block input streaming to PTY (e.g., during modal display)
let inputBlocked = false;

function setInputBlocked(blocked: boolean): void {
  inputBlocked = blocked;
  console.log(`[INPUT] Input ${blocked ? 'blocked' : 'unblocked'}`);
}

// Intercept Ctrl-V so the browser doesn't capture it as paste.
// Send the raw \x16 byte to Vim for block visual mode.
term.attachCustomKeyEventHandler((event: KeyboardEvent): boolean => {
  if (event.type === 'keydown' && event.ctrlKey && event.key === 'v') {
    event.preventDefault();
    if (!inputBlocked) {
      sendMessage({ type: 'input', data: '\x16' });
    }
    return false;
  }
  return true;
});

term.onData((data: string) => {
  if (inputBlocked) {
    return; // Don't send keystrokes to PTY when blocked
  }
  sendMessage({ type: 'input', data });
});

window.addEventListener('resize', () => {
  fitAddon.fit();
  sendMessage({ type: 'resize', cols: term.cols, rows: term.rows });
});

// ============================================================================
// UI Interactions
// ============================================================================

document.getElementById('terminal')?.addEventListener('click', () => {
  term.focus();
});

term.focus();

// Project status badge — pull version from server (sourced from package.json)
fetch('/api/health')
  .then((res) => (res.ok ? res.json() : null))
  .then((data) => {
    if (!data?.version) return;
    const el = document.getElementById('project-status');
    if (el) el.textContent = `Beta ${data.version}`;
  })
  .catch((err) => console.error('[VERSION] Failed to fetch app version:', err));

// Sidebar toggle
const menuToggle = document.getElementById('menu-toggle');
const sidebar = document.getElementById('sidebar');
const mainContent = document.getElementById('main-content');

menuToggle?.addEventListener('click', () => {
  sidebar?.classList.toggle('collapsed');
  mainContent?.classList.toggle('sidebar-collapsed');

  setTimeout(() => {
    fitAddon.fit();
    sendMessage({ type: 'resize', cols: term.cols, rows: term.rows });
  }, 300);
});


// ============================================================================
// Auth Required Modal
// ============================================================================

let pendingLessonId: string | null = null;

/**
 * Show the auth modal when trying to access a locked lesson
 */
function showAuthModal(lessonId: string): void {
  pendingLessonId = lessonId;
  const modal = document.getElementById('auth-modal');
  const lessonSpan = document.getElementById('auth-modal-lesson');

  if (modal && lessonSpan) {
    lessonSpan.textContent = lessonId;
    modal.classList.remove('hidden');
  }
}

/**
 * Hide the auth modal
 */
function hideAuthModal(): void {
  pendingLessonId = null;
  const modal = document.getElementById('auth-modal');
  modal?.classList.add('hidden');
  term.focus();
}

/**
 * Update lock icons on sidebar based on auth and payment state
 */
function updateLessonLockStates(): void {
  const currentUser = getCurrentUser();
  const navItems = document.querySelectorAll<HTMLElement>('.nav-item[data-lesson]');

  navItems.forEach(item => {
    const lessonId = item.getAttribute('data-lesson');
    const lockIcon = item.querySelector('.lesson-lock');

    if (!lessonId) return;

    // Show lock if: not signed in and lesson requires auth, OR signed in but lesson is paid and user hasn't paid
    const needsAuth = !isPublicLesson(lessonId) && !currentUser;
    const needsPayment = currentUser && !isFreeLesson(lessonId) && !userHasPaid;

    if (needsAuth || needsPayment) {
      lockIcon?.classList.remove('hidden');
    } else {
      lockIcon?.classList.add('hidden');
    }
  });
}

/**
 * Show or hide the "Pro" badge based on payment status
 */
function updatePaidBadge(): void {
  const badge = document.getElementById('paid-badge');
  if (userHasPaid) {
    badge?.classList.remove('hidden');
  } else {
    badge?.classList.add('hidden');
  }
}

// Set up auth modal event listeners
document.addEventListener('DOMContentLoaded', () => {
  const authModalClose = document.getElementById('auth-modal-close');
  const authModalSkip = document.getElementById('auth-modal-skip');
  const authModalSignin = document.getElementById('auth-modal-signin');

  authModalClose?.addEventListener('click', hideAuthModal);
  authModalSkip?.addEventListener('click', hideAuthModal);

  authModalSignin?.addEventListener('click', async () => {
    const signinBtn = authModalSignin;
    if (signinBtn) {
      signinBtn.textContent = 'Signing in...';
      signinBtn.setAttribute('disabled', 'true');
    }

    try {
      await signInWithGoogle();
      hideAuthModal();
      // pendingLessonId will be handled by auth_success message handler
    } catch (error) {
      console.error('[AUTH] Sign-in failed:', error);
    } finally {
      if (signinBtn) {
        signinBtn.innerHTML = '<span class="material-symbols-outlined text-sm">login</span> Sign in with Google';
        signinBtn.removeAttribute('disabled');
      }
    }
  });

  // Close auth modal on Escape
  document.addEventListener('keydown', (e) => {
    const modal = document.getElementById('auth-modal');
    if (modal && !modal.classList.contains('hidden') && e.key === 'Escape') {
      hideAuthModal();
    }
  });
});

// ============================================================================
// Payment Required Modal
// ============================================================================

/**
 * Show the payment modal when trying to access a paid lesson
 */
function showPaymentModal(lessonId: string): void {
  pendingLessonId = lessonId;
  const modal = document.getElementById('payment-modal');
  const lessonSpan = document.getElementById('payment-modal-lesson');

  if (modal && lessonSpan) {
    lessonSpan.textContent = lessonId;
    modal.classList.remove('hidden');
  }
}

/**
 * Show an error message inside the payment modal
 */
function showPaymentError(message: string): void {
  const modal = document.getElementById('payment-modal');
  if (!modal) return;

  let errorEl = document.getElementById('payment-modal-error');
  if (!errorEl) {
    errorEl = document.createElement('p');
    errorEl.id = 'payment-modal-error';
    errorEl.className = 'text-red-400 text-sm mt-2';
    const buyBtn = document.getElementById('payment-modal-buy');
    buyBtn?.parentElement?.insertBefore(errorEl, buyBtn.nextSibling);
  }
  errorEl.textContent = message;
}

/**
 * Hide the payment modal
 */
function hidePaymentModal(): void {
  pendingLessonId = null;
  const modal = document.getElementById('payment-modal');
  modal?.classList.add('hidden');
  const errorEl = document.getElementById('payment-modal-error');
  if (errorEl) errorEl.textContent = '';
  term.focus();
}

// Set up payment modal event listeners
document.addEventListener('DOMContentLoaded', () => {
  const paymentModalClose = document.getElementById('payment-modal-close');
  const paymentModalSkip = document.getElementById('payment-modal-skip');
  const paymentModalBuy = document.getElementById('payment-modal-buy');

  paymentModalClose?.addEventListener('click', hidePaymentModal);
  paymentModalSkip?.addEventListener('click', hidePaymentModal);

  paymentModalBuy?.addEventListener('click', async () => {
    const buyBtn = paymentModalBuy;
    if (buyBtn) {
      buyBtn.textContent = 'Redirecting...';
      buyBtn.setAttribute('disabled', 'true');
    }

    try {
      const token = await getIdToken();
      if (!token) return;

      const res = await fetch('/api/billing/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        window.location.href = data.url;
      } else {
        const data = await res.json().catch(() => null);
        if (res.status === 400 && data?.error === 'already_paid') {
          // User already has access — refresh status and close modal
          await fetchPaymentStatus();
          updateLessonLockStates();
          updatePaidBadge();
          hidePaymentModal();
          return;
        }
        showPaymentError('Checkout failed. Please try again.');
      }
    } catch (error) {
      console.error('[BILLING] Checkout error:', error);
      showPaymentError('Network error. Please check your connection.');
    } finally {
      if (buyBtn) {
        buyBtn.innerHTML = '<span class="material-symbols-outlined text-sm">shopping_cart</span> Buy Lifetime Access';
        buyBtn.removeAttribute('disabled');
      }
    }
  });

  // Close payment modal on Escape
  document.addEventListener('keydown', (e) => {
    const modal = document.getElementById('payment-modal');
    if (modal && !modal.classList.contains('hidden') && e.key === 'Escape') {
      hidePaymentModal();
    }
  });

  // Handle checkout return query params
  const params = new URLSearchParams(window.location.search);
  const checkoutStatus = params.get('checkout');
  if (checkoutStatus === 'success') {
    // Verify payment server-side instead of trusting the URL param
    fetchPaymentStatus().then(() => {
      updateLessonLockStates();
      updatePaidBadge();
    });
    // Clean up URL
    window.history.replaceState({}, '', window.location.pathname);
  } else if (checkoutStatus === 'canceled') {
    window.history.replaceState({}, '', window.location.pathname);
  }

});

// ============================================================================
// Viewport Warning Modal
// ============================================================================

const MIN_VIEWPORT_WIDTH = 1280;

/**
 * Check if viewport is below minimum width
 */
function isViewportTooSmall(): boolean {
  return window.innerWidth < MIN_VIEWPORT_WIDTH;
}

/**
 * Show the viewport warning modal
 */
function showViewportModal(): void {
  const modal = document.getElementById('viewport-modal');
  const currentWidthEl = document.getElementById('viewport-current-width');

  if (modal) {
    if (currentWidthEl) {
      currentWidthEl.textContent = `${window.innerWidth}px`;
    }
    modal.classList.remove('hidden');
  }
}

/**
 * Hide the viewport warning modal
 */
function hideViewportModal(): void {
  const modal = document.getElementById('viewport-modal');
  modal?.classList.add('hidden');
  term.focus();
}

/**
 * Check viewport and show modal if necessary
 */
function checkViewport(): void {
  // Don't show if user has dismissed this session
  if (sessionStorage.getItem('viewport-modal-dismissed') === 'true') {
    return;
  }

  if (isViewportTooSmall()) {
    showViewportModal();
  } else {
    hideViewportModal();
  }
}

// ============================================================================
// Email Feedback Card
// ============================================================================

const EMAIL_DISMISS_KEY = 'email-feedback-dismissed';

function showEmailFeedback(): void {
  if (sessionStorage.getItem(EMAIL_DISMISS_KEY) === 'true') return;
  document.getElementById('email-feedback')?.classList.remove('hidden');
}

function hideEmailFeedback(): void {
  document.getElementById('email-feedback')?.classList.add('hidden');
  sessionStorage.setItem(EMAIL_DISMISS_KEY, 'true');
  term.focus();
}

// Set up viewport modal event listeners
document.addEventListener('DOMContentLoaded', () => {
  const dismissBtn = document.getElementById('viewport-modal-dismiss');
  const continueBtn = document.getElementById('viewport-modal-continue');

  document.getElementById('email-feedback-dismiss')
    ?.addEventListener('click', hideEmailFeedback);
  showEmailFeedback();

  dismissBtn?.addEventListener('click', () => {
    sessionStorage.setItem('viewport-modal-dismissed', 'true');
    hideViewportModal();
  });

  continueBtn?.addEventListener('click', () => {
    sessionStorage.setItem('viewport-modal-dismissed', 'true');
    hideViewportModal();
  });

  // Check viewport on load
  checkViewport();

  // Update current width display on resize
  let resizeTimeout: ReturnType<typeof setTimeout> | null = null;
  window.addEventListener('resize', () => {
    // Debounce resize handler
    if (resizeTimeout) {
      clearTimeout(resizeTimeout);
    }
    resizeTimeout = setTimeout(() => {
      const currentWidthEl = document.getElementById('viewport-current-width');
      if (currentWidthEl) {
        currentWidthEl.textContent = `${window.innerWidth}px`;
      }
      checkViewport();
    }, 100);
  });
});

// ============================================================================
// Lesson Loading
// ============================================================================

const DEFAULT_LESSON_ID = '1-hjkl-basics';
const TRACK_CHOSEN_KEY = 'vimdojo:track-chosen';
let currentLessonId = DEFAULT_LESSON_ID;

/**
 * Fetch lesson manifest and render sidebar navigation
 */
async function loadLessonManifest(): Promise<void> {
  try {
    const response = await fetch('/api/lessons/manifest');
    if (!response.ok) {
      throw new Error(`Failed to load manifest: ${response.status}`);
    }
    lessonManifest = await response.json() as LessonManifest;
    renderLessonNav(lessonManifest);
  } catch (err) {
    console.error('[MANIFEST] Error loading lesson manifest:', err);
  }
}

/**
 * Render lesson navigation items from manifest, grouped into collapsible sections
 */
const collapsedGroups = new Set<string>();
let collapsedGroupsInitialized = false;
let activeLessonTab: 'motions' | 'neovim' = 'motions';
let activeLessonTabInitialized = false;

function groupBelongsToTab(
  group: LessonManifest['groups'][number],
  lessonMap: Map<string, LessonManifestEntry>,
): 'motions' | 'neovim' {
  const isNvim = group.lessons.some(id => {
    const lesson = lessonMap.get(id);
    if (!lesson) return false;
    return getLessonEngine(`${lesson.lesson_id}-${lesson.name}`) !== 'vim';
  });
  return isNvim ? 'neovim' : 'motions';
}

/**
 * Count free vs. total lessons visible under a tab, straight from the
 * manifest — computed rather than hardcoded so this can't go stale again as
 * lessons are added, hidden, or re-priced.
 */
function getTabLessonStats(tab: 'motions' | 'neovim'): { free: number; total: number } {
  if (!lessonManifest) return { free: 0, total: 0 };
  const lessonMap = new Map<string, LessonManifestEntry>();
  lessonManifest.lessons.forEach(l => lessonMap.set(l.lesson_id, l));
  const ids = new Set<string>();
  lessonManifest.groups.forEach(g => {
    if (groupBelongsToTab(g, lessonMap) === tab) {
      g.lessons.forEach(id => ids.add(id));
    }
  });
  let free = 0;
  ids.forEach(id => {
    if (lessonMap.get(id)?.is_free) free++;
  });
  return { free, total: ids.size };
}

function syncLessonTabButtons(): void {
  document.querySelectorAll<HTMLElement>('#lesson-tabs [data-lesson-tab]').forEach(btn => {
    const isActive = btn.getAttribute('data-lesson-tab') === activeLessonTab;
    btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
  });
  const banner = document.getElementById('lesson-tabs-banner-text');
  if (banner) {
    const { free, total } = getTabLessonStats(activeLessonTab);
    if (activeLessonTab === 'neovim') {
      banner.textContent = free < total
        ? `sampling ${free} of ${total} neovim → unlock the rest`
        : `${total} neovim lesson${total === 1 ? '' : 's'} free → more on the way`;
    } else {
      banner.textContent = free < total
        ? `first ${free} motions free → unlock all ${total}`
        : `all ${total} motions free`;
    }
  }
}

function setupLessonTabs(): void {
  const container = document.getElementById('lesson-tabs');
  if (!container) return;
  container.addEventListener('click', (event) => {
    const target = event.target as HTMLElement | null;
    const btn = target?.closest<HTMLElement>('[data-lesson-tab]');
    if (!btn || !container.contains(btn)) return;
    const tab = btn.getAttribute('data-lesson-tab');
    if (tab !== 'motions' && tab !== 'neovim') return;
    if (tab === activeLessonTab) return;
    activeLessonTab = tab;
    syncLessonTabButtons();
    // Re-pick the default-expanded group for the newly visible tab.
    collapsedGroupsInitialized = false;
    if (lessonManifest) renderLessonNav(lessonManifest);
  });
}

function renderLessonNav(manifest: LessonManifest): void {
  const nav = document.getElementById('lesson-nav');
  if (!nav) return;

  nav.innerHTML = '';

  // Build a lookup map for lessons by ID
  const lessonMap = new Map<string, LessonManifestEntry>();
  manifest.lessons.forEach(l => lessonMap.set(l.lesson_id, l));

  // On first render, pick the active tab based on the current lesson
  if (!activeLessonTabInitialized) {
    activeLessonTabInitialized = true;
    if (currentLessonId && getLessonEngine(currentLessonId) !== 'vim') {
      activeLessonTab = 'neovim';
    }
    syncLessonTabButtons();
  }

  const visibleGroups = manifest.groups.filter(
    g => groupBelongsToTab(g, lessonMap) === activeLessonTab,
  );

  // On first render (or after a tab switch), collapse all visible groups except
  // the one containing the current lesson — falling back to the first visible group.
  if (!collapsedGroupsInitialized) {
    collapsedGroupsInitialized = true;
    manifest.groups.forEach(g => collapsedGroups.add(g.name));
    let activeGroup = currentLessonId
      ? visibleGroups.find(g =>
          g.lessons.some(id => currentLessonId!.startsWith(id + '-')),
        )
      : undefined;
    if (!activeGroup) activeGroup = visibleGroups[0];
    if (activeGroup) collapsedGroups.delete(activeGroup.name);
  }

  visibleGroups.forEach(group => {
    const isCollapsed = collapsedGroups.has(group.name);

    // Group header
    const header = document.createElement('div');
    header.className = 'cursor-pointer px-4 py-2 flex items-center gap-2 text-outline text-xs uppercase tracking-wider hover:text-[#8eff71] transition-all select-none';
    header.innerHTML = `
      <span class="material-symbols-outlined text-xs transition-transform ${isCollapsed ? '-rotate-90' : ''}" style="font-size: 16px;">expand_more</span>
      <span>${group.name}</span>
    `;
    header.addEventListener('click', () => {
      const wasCollapsed = collapsedGroups.has(group.name);
      // Accordion: collapse all groups
      manifest.groups.forEach(g => collapsedGroups.add(g.name));
      // If clicked group was collapsed, expand it
      if (wasCollapsed) {
        collapsedGroups.delete(group.name);
      }
      renderLessonNav(manifest);
    });
    nav.appendChild(header);

    // Lesson items within this group
    if (!isCollapsed) {
      group.lessons.forEach(lessonId => {
        const lesson = lessonMap.get(lessonId);
        if (!lesson) return;

        const fullId = `${lesson.lesson_id}-${lesson.name}`;
        const div = document.createElement('div');
        div.className = 'group cursor-pointer text-[#767575] hover:bg-[#20201f] hover:text-[#8eff71] pl-8 py-3 flex items-center gap-3 transition-all nav-item';
        div.setAttribute('data-lesson', fullId);
        if (!lesson.is_free) {
          div.setAttribute('data-requires-auth', 'true');
        }

        div.innerHTML = `
          <span class="text-xs font-mono text-outline w-5 lesson-number">${lesson.lesson_id}</span>
          <span class="text-sm tracking-wide lesson-name">${lesson.title}</span>
          <span class="ml-auto mr-4 lesson-status"></span>
          ${!lesson.is_free ? `
            <span class="lesson-lock mr-2 text-outline/50 hidden">
              <span class="material-symbols-outlined text-sm">lock</span>
            </span>
          ` : ''}
        `;

        div.addEventListener('click', () => selectLesson(fullId));
        nav.appendChild(div);
      });
    }
  });

  // Update sidebar status after rendering
  updateSidebarLessonStatus();
  updateLessonLockStates();
}

async function loadLessonInstructions(lessonId: string): Promise<void> {
  try {
    const token = await getIdToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`/api/lessons/${lessonId}/instructions`, { headers });
    if (!response.ok) {
      throw new Error(`Failed to load lesson: ${response.status}`);
    }
    const lesson = await response.json() as LessonInstructions;
    renderLessonContent(lesson);
    // Update terminal title with lesson topic
    const terminalTitle = document.getElementById('terminal-title');
    if (terminalTitle) {
      terminalTitle.textContent = lesson.topic;
    }
    // Initialize step progress if multi-step lesson
    if (lesson.totalSteps && lesson.currentStep) {
      updateStepProgress(lesson.currentStep, lesson.totalSteps);
    }
  } catch (err) {
    console.error('Error loading lesson instructions:', err);
  }
}

async function loadStepInstructions(lessonId: string, step: number): Promise<void> {
  try {
    const token = await getIdToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`/api/lessons/${lessonId}/instructions?step=${step}`, { headers });
    if (!response.ok) {
      throw new Error(`Failed to load step instructions: ${response.status}`);
    }
    const stepInfo = await response.json() as LessonInstructions;
    renderLessonContent(stepInfo);
  } catch (err) {
    console.error('Error loading step instructions:', err);
  }
}

function updateStepProgress(current: number, total: number): void {
  const progressEl = document.getElementById('step-progress');
  if (progressEl) {
    progressEl.textContent = `Step ${current} of ${total}`;
    progressEl.classList.remove('hidden');
  }

  // Update task progress text
  const taskProgressText = document.getElementById('task-progress');
  if (taskProgressText) {
    const currentPadded = String(current).padStart(2, '0');
    const totalPadded = String(total).padStart(2, '0');
    taskProgressText.textContent = `${currentPadded} / ${totalPadded}`;
  }

  // Update task progress bar segments
  const taskProgressBar = document.getElementById('task-progress-bar');
  if (taskProgressBar) {
    taskProgressBar.innerHTML = '';
    for (let i = 0; i < total; i++) {
      const segment = document.createElement('div');
      segment.className = 'flex-1 ' + (i < current ? 'bg-primary' : 'bg-surface-container-highest');
      taskProgressBar.appendChild(segment);
    }
  }
}

// Hints for the currently displayed step, revealed progressively via
// step_hint events (see revealHint()) rather than shown all at once.
let currentStepHints: string[] = [];
let revealedHints = new Set<number>();

function renderLessonContent(lesson: LessonInstructions): void {
  const lessonNumber = document.getElementById('lesson-number');
  const lessonTopic = document.getElementById('lesson-topic');
  const lessonTitle = document.getElementById('lesson-title');
  const lessonContent = document.getElementById('lesson-content');

  if (lessonNumber) lessonNumber.textContent = `LESSON ${lesson.lesson_id}`;
  if (lessonTopic) lessonTopic.textContent = lesson.topic;
  if (lessonTitle) lessonTitle.innerHTML = lesson.title;
  if (lessonContent) lessonContent.innerHTML = lesson.content;
  renderRelatedResources(lesson.relatedResources || []);

  currentStepHints = lesson.hints || [];
  revealedHints = new Set();
}

/**
 * Reveals hints[hintIndex] for the current step by appending it below the
 * existing instructions content, in response to a step_hint event. Distinct
 * from step_start's full renderLessonContent() replace - this only adds to
 * what's already shown, since the step itself hasn't advanced.
 */
function revealHint(hintIndex: number): void {
  if (revealedHints.has(hintIndex)) return; // idempotent, in case a lesson's autocmd double-fires
  if (hintIndex < 0 || hintIndex >= currentStepHints.length) return;
  revealedHints.add(hintIndex);
  document.getElementById('lesson-content')
    ?.insertAdjacentHTML('beforeend', currentStepHints[hintIndex]);
}

function renderRelatedResources(resources: RelatedResource[]): void {
  const slot = document.getElementById('lesson-resources');
  if (!slot) return;

  if (resources.length === 0) {
    slot.classList.add('hidden');
    slot.innerHTML = '';
    return;
  }

  slot.classList.remove('hidden');
  slot.classList.remove('pulse-stopped');
  slot.addEventListener(
    'mouseenter',
    () => slot.classList.add('pulse-stopped'),
    { once: true },
  );
  const chips = resources
    .map((r) => {
      const fullTitle = r.titleAccent ? `${r.title} ${r.titleAccent}` : r.title;
      const safeTitle = fullTitle.replace(/[<>"']/g, (c) =>
        ({ '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] || c),
      );
      const safeSlug = encodeURIComponent(r.slug);
      return `<a href="/resources/${safeSlug}/" target="_blank" rel="noopener"
        class="resource-pulse group inline-flex items-center gap-2 px-3 py-1.5 bg-primary/5 border border-primary/30 hover:border-primary hover:bg-primary/10 transition-colors text-xs font-mono text-primary">
        <span class="material-symbols-outlined text-[14px]">menu_book</span>
        <span class="font-bold normal-case tracking-tight">${safeTitle}</span>
        <span class="material-symbols-outlined text-[12px] text-outline group-hover:text-primary transition-colors">open_in_new</span>
      </a>`;
    })
    .join('');

  slot.innerHTML =
    '<div class="text-[10px] uppercase tracking-widest text-outline font-mono mb-2">// Related setup</div>' +
    `<div class="flex flex-wrap gap-2">${chips}</div>`;
}

// ============================================================================
// Lesson Progress Management
// ============================================================================

const COMPLETED_LESSONS_KEY = branding.completedLessonsKey;

/**
 * Get set of completed lesson IDs from localStorage
 */
function getCompletedLessons(): Set<string> {
  try {
    const stored = localStorage.getItem(COMPLETED_LESSONS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as string[];
      let migrated = false;
      const normalized = parsed.map(id => {
        const match = id.match(/^0(\d+)(-.*)$/);
        if (match) {
          migrated = true;
          return `${match[1]}${match[2]}`;
        }
        return id;
      });
      if (migrated) {
        localStorage.setItem(COMPLETED_LESSONS_KEY, JSON.stringify(normalized));
      }
      return new Set(normalized);
    }
  } catch (err) {
    console.error('[PROGRESS] Error reading completed lessons:', err);
  }
  return new Set();
}

/**
 * Mark a lesson as completed and save to localStorage
 */
function markLessonCompleted(lessonId: string): void {
  const completed = getCompletedLessons();
  completed.add(lessonId);
  localStorage.setItem(COMPLETED_LESSONS_KEY, JSON.stringify([...completed]));
  console.log('[PROGRESS] Marked lesson completed:', lessonId);
  updateSidebarLessonStatus();
}

// ----------------------------------------------------------------------------
// Drill progress (mastery tier per lesson)
// ----------------------------------------------------------------------------

const DRILL_PROGRESS_KEY = 'vim-sensei-drill-progress';

function tierRankClient(tier: DrillTier): number {
  return tier === 'gold' ? 3 : tier === 'silver' ? 2 : 1;
}

function getDrillProgressLocal(): Record<string, DrillTier> {
  try {
    const stored = localStorage.getItem(DRILL_PROGRESS_KEY);
    if (!stored) return {};
    return JSON.parse(stored) as Record<string, DrillTier>;
  } catch (err) {
    console.error('[DRILL] Error reading drill progress:', err);
    return {};
  }
}

function setDrillTierLocal(lessonId: string, tier: DrillTier): void {
  const current = getDrillProgressLocal();
  const existing = current[lessonId];
  if (!existing || tierRankClient(tier) > tierRankClient(existing)) {
    current[lessonId] = tier;
    localStorage.setItem(DRILL_PROGRESS_KEY, JSON.stringify(current));
  }
  updateSidebarLessonStatus();
}

// Pull server-side drill tiers and merge into localStorage so the sidebar
// reflects best-ever data after sign-in. Best-of-both wins on conflict.
async function syncDrillProgressFromServer(): Promise<void> {
  const token = await getIdToken();
  if (!token) return;
  try {
    const res = await fetch('/api/drills/progress', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return;
    const data = (await res.json()) as { progress: Record<string, DrillTier> };
    const local = getDrillProgressLocal();
    for (const [lessonId, serverTier] of Object.entries(data.progress)) {
      const localTier = local[lessonId];
      if (!localTier || tierRankClient(serverTier) > tierRankClient(localTier)) {
        local[lessonId] = serverTier;
      }
    }
    localStorage.setItem(DRILL_PROGRESS_KEY, JSON.stringify(local));
    updateSidebarLessonStatus();
  } catch (err) {
    console.error('[DRILL] Failed to sync drill progress:', err);
  }
}

/**
 * Update sidebar to reflect current lesson status
 */
// Renders the drill badge slot for a sidebar lesson item.
//   earned tier  → colored G/S/B chip
//   has drills, not yet earned → outlined "D" chip (same shape, dimmed)
//   no drills available        → empty string
function renderDrillBadge(lessonId: string, drillProgress: Record<string, DrillTier>): string {
  const tier = drillProgress[lessonId];
  if (tier) {
    const color =
      tier === 'gold'
        ? 'bg-[#fabd2f]/15 text-[#fabd2f] border border-[#fabd2f]/40'
        : tier === 'silver'
        ? 'bg-[#c0c0c0]/15 text-[#c0c0c0] border border-[#c0c0c0]/40'
        : 'bg-[#cd7f32]/15 text-[#cd7f32] border border-[#cd7f32]/40';
    return `<span class="ml-1.5 inline-block px-1 py-0.5 text-[9px] font-mono font-bold uppercase tracking-tight ${color}">${tier[0].toUpperCase()}</span>`;
  }
  if (hasDrills(lessonId)) {
    return `<span class="ml-1.5 inline-block px-1 py-0.5 text-[9px] font-mono font-bold uppercase tracking-tight text-outline/50 border border-outline/30" title="Drills available">D</span>`;
  }
  return '';
}

function updateSidebarLessonStatus(): void {
  const completed = getCompletedLessons();
  const drillProgress = getDrillProgressLocal();
  const navItems = document.querySelectorAll<HTMLElement>('.nav-item[data-lesson]');

  navItems.forEach(item => {
    const lessonId = item.getAttribute('data-lesson');
    if (!lessonId) return;

    const statusEl = item.querySelector('.lesson-status');
    const isCompleted = completed.has(lessonId);
    const isActive = lessonId === currentLessonId;

    // Reset classes
    item.classList.remove('text-[#8eff71]', 'border-l-2', 'border-[#8eff71]', 'bg-[#262626]/20');
    item.classList.add('text-[#767575]');
    item.querySelector('.lesson-name')?.classList.remove('font-medium');

    if (isActive) {
      // Active lesson styling
      item.classList.remove('text-[#767575]');
      item.classList.add('text-[#8eff71]', 'border-l-2', 'border-[#8eff71]', 'bg-[#262626]/20');
      item.querySelector('.lesson-name')?.classList.add('font-medium');
      if (statusEl) {
        statusEl.innerHTML = '<div class="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></div>';
      }
    } else if (isCompleted) {
      // Completed lesson styling
      if (statusEl) {
        statusEl.innerHTML = `<span class="inline-flex items-center"><span class="material-symbols-outlined text-xs text-primary" style="font-variation-settings: 'FILL' 1;">check_circle</span>${renderDrillBadge(lessonId, drillProgress)}</span>`;
      }
    } else {
      // Default styling (not started) — but still surface "drills available"
      // for lessons that ship a drill pack, so the affordance is visible
      // before the user reaches lesson:complete.
      if (statusEl) {
        statusEl.innerHTML = renderDrillBadge(lessonId, drillProgress);
      }
    }
  });

  // Update overall progress
  const totalLessons = lessonManifest?.lessons.length ?? 0;
  const completedCount = completed.size;
  const progressPercent = Math.round((completedCount / totalLessons) * 100);

  const progressPercentEl = document.getElementById('progress-percent');
  const progressBarEl = document.getElementById('progress-bar');

  if (progressPercentEl) {
    progressPercentEl.textContent = `${progressPercent}%`;
  }
  if (progressBarEl) {
    progressBarEl.style.width = `${progressPercent}%`;
  }
}

/**
 * Select and start a lesson
 */
function selectLesson(lessonId: string): void {
  if (lessonId === currentLessonId) return;

  // Check if auth required (client-side hint - server will also enforce)
  if (!isPublicLesson(lessonId) && !getCurrentUser()) {
    showAuthModal(lessonId);
    return;
  }

  // If the user was in drill mode for a different lesson, clear that state.
  // The server clears its drill state on select_lesson too.
  inDrillMode = false;
  drillLiveKeystrokes = 0;
  const stepProgress = document.getElementById('step-progress');
  if (stepProgress) stepProgress.classList.add('hidden');

  console.log('[LESSON] Selecting lesson:', lessonId);
  currentLessonId = lessonId;

  // Unblock input for the new lesson
  setInputBlocked(false);

  // Wipe any modes/state (alt screen, cursor mode, attributes) left over
  // from the previous lesson's killed vim/nvim process.
  term.reset();

  // Update sidebar UI
  updateSidebarLessonStatus();

  // Load lesson instructions
  loadLessonInstructions(lessonId);

  // Show loading state in terminal
  term.write('\r\n\x1b[33mLoading lesson...\x1b[0m\r\n');

  // Push a fresh resize before select_lesson so the server uses up-to-date
  // dims when spawning nvim. Two rAFs let any in-flight layout (sidebar
  // collapse, instructions panel update) settle before we measure.
  // proposeDimensions reads the container directly instead of returning the
  // last value cached on the Terminal instance.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const dims = fitAddon.proposeDimensions();
      const cols = dims?.cols ?? term.cols;
      const rows = dims?.rows ?? term.rows;
      if (cols > 0 && rows > 0) {
        term.resize(cols, rows);
        sendMessage({ type: 'resize', cols, rows });
      }
      sendMessage({ type: 'select_lesson', lessonId });
    });
  });
}

// Lesson click handlers are set up dynamically in renderLessonNav()

// Initial lesson loading is now handled by auth state change listener
// loadLessonInstructions(currentLessonId);

// ============================================================================
// First-Visit Track Chooser Modal
// ============================================================================

function switchToNeovimTrack(lessonId: string): void {
  activeLessonTab = 'neovim';
  syncLessonTabButtons();
  collapsedGroupsInitialized = false;
  if (lessonManifest) renderLessonNav(lessonManifest);
  selectLesson(lessonId);
}

function hideTrackChooser(choice: 'motions' | 'neovim' | 'dismissed'): void {
  const modal = document.getElementById('track-chooser-modal');
  modal?.classList.add('hidden');
  try {
    localStorage.setItem(TRACK_CHOSEN_KEY, choice);
  } catch (err) {
    console.error('[TRACK] Could not persist track choice:', err);
  }
}

function maybeShowTrackChooser(): void {
  // Skip if user has already chosen (or dismissed) the modal previously.
  let alreadyChosen: string | null = null;
  try {
    alreadyChosen = localStorage.getItem(TRACK_CHOSEN_KEY);
  } catch (err) {
    console.error('[TRACK] localStorage unavailable:', err);
    return;
  }
  if (alreadyChosen) return;

  // Skip if user has progress — they've effectively already chosen by behavior.
  if (getCompletedLessons().size > 0) return;

  // Skip if we've been routed away from the default lesson (e.g., future deep-link).
  if (currentLessonId !== DEFAULT_LESSON_ID) return;

  const modal = document.getElementById('track-chooser-modal');
  if (!modal) return;

  const motionsBtn = document.getElementById('track-chooser-motions');
  const neovimBtn = document.getElementById('track-chooser-neovim');
  const dismissBtn = document.getElementById('track-chooser-dismiss');

  motionsBtn?.addEventListener('click', () => {
    // L1 is already loaded by default; just close the modal.
    hideTrackChooser('motions');
  });
  neovimBtn?.addEventListener('click', () => {
    hideTrackChooser('neovim');
    switchToNeovimTrack('31-lazyvim-sidebar');
  });
  dismissBtn?.addEventListener('click', () => {
    hideTrackChooser('dismissed');
  });

  modal.classList.remove('hidden');
}

// ============================================================================
// Branding Application
// ============================================================================

/**
 * Apply branding from config to DOM elements
 */
function applyBranding(): void {
  // Page title
  document.title = branding.pageTitle;

  // Header brand name
  const headerBrand = document.querySelector('header .font-headline');
  if (headerBrand) headerBrand.textContent = branding.name;

  // Sidebar heading
  const sidebarHeading = document.querySelector('#sidebar h2');
  if (sidebarHeading) sidebarHeading.textContent = branding.sidebarHeading;

  // Terminal header handle
  const terminalHandle = document.querySelector('#terminal-header .text-primary.tracking-widest');
  if (terminalHandle) terminalHandle.textContent = `${branding.handle} ${branding.year}`;

  // Status line
  const statusBadge = document.querySelector('#status-line .bg-primary.text-on-primary');
  if (statusBadge) statusBadge.textContent = branding.statusBadge;

  const statusTagline = document.querySelector('#status-line .bg-\\[\\#3c3836\\].text-\\[\\#ebdbb2\\]');
  if (statusTagline) statusTagline.textContent = branding.tagline;

  const statusHandle = document.querySelectorAll('#status-line .bg-\\[\\#3c3836\\].text-\\[\\#ebdbb2\\]')[1];
  if (statusHandle) statusHandle.textContent = branding.handle;
}

applyBranding();

// ============================================================================
// Authentication UI
// ============================================================================

/**
 * Update auth UI based on current user state
 */
function updateAuthUI(user: AuthUser | null): void {
  const signInBtn = document.getElementById('sign-in-btn');
  const userMenu = document.getElementById('user-menu');
  const userAvatar = document.getElementById('user-avatar') as HTMLImageElement | null;
  const userName = document.getElementById('user-name');

  if (user) {
    // User is signed in
    signInBtn?.classList.add('hidden');
    userMenu?.classList.remove('hidden');

    if (userAvatar && user.photoURL) {
      userAvatar.src = user.photoURL;
      userAvatar.alt = user.displayName || 'User';
    }
    if (userName) {
      userName.textContent = user.displayName || user.email || 'User';
    }
  } else {
    // User is signed out
    signInBtn?.classList.remove('hidden');
    userMenu?.classList.add('hidden');

    if (userAvatar) {
      userAvatar.src = '';
    }
    if (userName) {
      userName.textContent = '';
    }
  }
}

/**
 * Handle sign-in button click
 */
async function handleSignIn(): Promise<void> {
  const signInBtn = document.getElementById('sign-in-btn');
  if (signInBtn) {
    signInBtn.textContent = 'Signing in...';
    signInBtn.setAttribute('disabled', 'true');
  }

  try {
    await signInWithGoogle();
    // Auth state change listener will handle the rest
  } catch (error) {
    console.error('[AUTH] Sign-in failed:', error);
    term.write('\r\n\x1b[31mSign-in failed. Please try again.\x1b[0m\r\n');
  } finally {
    if (signInBtn) {
      signInBtn.innerHTML = '<span class="material-symbols-outlined text-sm">login</span> Sign in with Google';
      signInBtn.removeAttribute('disabled');
    }
  }
}

/**
 * Handle sign-out button click
 */
async function handleSignOut(): Promise<void> {
  try {
    // Close WebSocket first (intentional, so skip auto-reconnect in onclose)
    if (ws) {
      intentionalClose = true;
      ws.close();
      ws = null;
    }

    await signOut();

    // Full frontend reset
    resetToDefaultState();

    // Reconnect anonymously
    setTimeout(connect, 100);
  } catch (error) {
    console.error('[AUTH] Sign-out failed:', error);
  }
}

/**
 * Reset frontend to default state (used on sign-out)
 */
function resetToDefaultState(): void {
  console.log('[RESET] Resetting to default state');

  // Reset to default lesson
  currentLessonId = DEFAULT_LESSON_ID;

  // Clear terminal
  term.write('\x1b[2J\x1b[H'); // Clear screen and move cursor to home
  term.write('\x1b[33mSigned out. Loading default lesson...\x1b[0m\r\n');

  // Unblock input
  setInputBlocked(false);

  // Hide any open modals/overlays (without side effects)
  hideAuthModal();
  hideOverlay();

  // Update sidebar UI
  updateSidebarLessonStatus();
  updateLessonLockStates();

  // Reload default lesson instructions
  loadLessonInstructions(DEFAULT_LESSON_ID);

  // Update terminal title
  const terminalTitle = document.getElementById('terminal-title');
  if (terminalTitle) {
    terminalTitle.textContent = 'lesson loading...';
  }

  // Reset step progress indicator
  const stepProgress = document.getElementById('step-progress');
  stepProgress?.classList.add('hidden');

  const taskProgress = document.getElementById('task-progress');
  if (taskProgress) {
    taskProgress.textContent = '-- / --';
  }

  const taskProgressBar = document.getElementById('task-progress-bar');
  if (taskProgressBar) {
    taskProgressBar.innerHTML = '';
  }
}

// ============================================================================
// Auth Initialization
// ============================================================================

document.addEventListener('DOMContentLoaded', async () => {
  // Initialize Firebase Auth
  initAuth();

  // Load lesson manifest and render sidebar
  setupLessonTabs();
  await loadLessonManifest();

  // Wait for Firebase to determine initial auth state (restores cached credentials)
  await authReady();
  console.log('[AUTH] Initial auth state ready, user:', getCurrentUser()?.email || 'anonymous');

  trackEvent(
    'app_open',
    { ...acquisitionProps(), viewportTooSmall: isViewportTooSmall() },
    await getIdToken(),
  );

  // First-visit track chooser — runs once per browser, gated on no prior progress.
  maybeShowTrackChooser();

  // Listen for auth state changes (for sign-in/sign-out AFTER initial load)
  onAuthStateChanged(async (user) => {
    updateAuthUI(user);

    if (user) {
      console.log('[AUTH] User signed in:', user.email || user.uid);
      await fetchPaymentStatus();
      updateLessonLockStates();
      updatePaidBadge();
      // Pull best-ever drill tiers from Firestore (merges into localStorage).
      syncDrillProgressFromServer();
      // Send auth token to upgrade existing session (no reconnect needed)
      if (ws && ws.readyState === WebSocket.OPEN) {
        const token = await getIdToken();
        if (token) {
          console.log('[AUTH] Sending auth token to upgrade session...');
          sendMessage({ type: 'authenticate', token });
        }
      }
    } else {
      console.log('[AUTH] User signed out or not signed in');
      userHasPaid = false;
      updateLessonLockStates();
      updatePaidBadge();
    }
  });

  // Now connect - auth state is known, so connect() can include token if user is signed in
  connect();

  // Load lesson instructions
  loadLessonInstructions(currentLessonId);

  // Sign-in button handler
  const signInBtn = document.getElementById('sign-in-btn');
  signInBtn?.addEventListener('click', handleSignIn);

  // Sign-out button handler
  const signOutBtn = document.getElementById('sign-out-btn');
  signOutBtn?.addEventListener('click', handleSignOut);
});
